/**
 * Нормализация текста для поиска на узбекском (латиница и кириллица), каракалпакском и русском.
 *
 * Идея: и индексируемый текст, и запрос приводятся к одному «поисковому ключу» в латинице
 * без диакритики. Это не морфологический анализ и не семантический поиск — только
 * транслитерация, свёртка вариантов написания, словарь синонимов и (в БД) триграммы
 * для опечаток. Ограничения описаны в docs/search.md.
 */

const CYRILLIC_TO_LATIN: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'j', з: 'z', и: 'i', й: 'y',
  к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f',
  х: 'x', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sh', ъ: '', ы: 'i', ь: '', э: 'e', ю: 'yu', я: 'ya',
  // узбекская кириллица
  қ: 'q', ғ: 'g', ҳ: 'h', ў: 'o',
  // каракалпакская кириллица
  ә: 'a', ө: 'o', ү: 'u', ң: 'n', і: 'i', ұ: 'u',
};

/** Латинские буквы каракалпакского/узбекского алфавитов с диакритикой → базовые. */
const LATIN_FOLD: Record<string, string> = {
  á: 'a', à: 'a', â: 'a', ä: 'a',
  ǵ: 'g', ğ: 'g',
  ı: 'i', í: 'i', ì: 'i', î: 'i', ï: 'i',
  ń: 'n', ñ: 'n',
  ó: 'o', ò: 'o', ô: 'o', ö: 'o', õ: 'o',
  ú: 'u', ù: 'u', û: 'u', ü: 'u',
  é: 'e', è: 'e', ê: 'e', ë: 'e',
  ş: 'sh', ç: 'ch',
};

/** Все варианты апострофа (o‘, g‘, oʻ, o', o`, ’) — удаляются: o‘ → o, g‘ → g. */
const APOSTROPHES = /[‘’ʻʼʽ`´'ʼ]/g;

/**
 * Фонетические свёртки для поискового ключа: q≈k (Qaraqalpaq / Каракалпак),
 * w≈v (каракалпакское w / узбекское v), удвоенные буквы → одна (опечатки «kvartiraa»).
 */
function phoneticFold(word: string): string {
  return word.replace(/q/g, 'k').replace(/w/g, 'v').replace(/([a-z])\1+/g, '$1');
}

/** Приводит произвольный текст к нижнему регистру, латинице без диакритики, без пунктуации. */
export function normalizeText(input: string): string {
  if (!input) return '';
  let s = input.normalize('NFKC').toLowerCase();
  s = s.replace(APOSTROPHES, '');
  let out = '';
  for (const ch of s) {
    const cyr = CYRILLIC_TO_LATIN[ch];
    if (cyr !== undefined) {
      out += cyr;
      continue;
    }
    const lat = LATIN_FOLD[ch];
    if (lat !== undefined) {
      out += lat;
      continue;
    }
    // Оставшаяся диакритика: разложить и выбросить комбинирующие знаки.
    out += ch.normalize('NFD').replace(/[̀-ͯ]/g, '');
  }
  return out
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Поисковый ключ: нормализация + фонетическая свёртка каждого слова. */
export function searchKey(input: string): string {
  return normalizeText(input)
    .split(' ')
    .filter(Boolean)
    .map(phoneticFold)
    .join(' ');
}

/**
 * Словарь синонимов (в исходном написании; нормализуется при загрузке).
 * Группа = взаимозаменяемые слова. Расширяется в docs/search.md / через код.
 */
/**
 * Словарь синонимов по умолчанию. В работающей системе словарь хранится в БД (таблица search_synonym_groups,
 * редактируется в админ-панели → «Поиск») и подставляется через setSynonymGroups(); этот список — начальное
 * заполнение (миграция) и запасной вариант, если в БД словаря нет.
 */
export const DEFAULT_SYNONYM_GROUPS: string[][] = [
  ['telefon', 'телефон', 'smartfon', 'смартфон', 'mobil', 'мобильный', 'phone'],
  ['iphone', 'айфон', 'ayfon'],
  ['mashina', 'машина', 'avtomobil', 'автомобиль', 'avto', 'авто', 'car', 'moshin'],
  ['kvartira', 'квартира', 'uy-joy', 'xonadon'],
  ['uy', 'дом', 'hovli', 'hawli', 'жилой дом'],
  ['noutbuk', 'ноутбук', 'laptop', 'notebook'],
  ['kompyuter', 'компьютер', 'pc'],
  ['televizor', 'телевизор', 'tv'],
  ['muzlatkich', 'холодильник', 'xolodilnik', 'tońazıtqısh'],
  ['kir yuvish mashinasi', 'стиральная машина', 'stiralka', 'стиралка'],
  ['ijara', 'аренда', 'arenda', 'ijaraga'],
  ['ish', 'работа', 'vakansiya', 'вакансия', 'jumıs'],
  ['sigir', 'корова', 'siyir'],
  ["qo'y", 'овца', 'baran', 'баран', 'qoy'],
  ['ot', 'лошадь', 'конь'],
  ['traktor', 'трактор'],
  ['velosiped', 'велосипед'],
  ['mebel', 'мебель'],
  ['divan', 'диван'],
  ['ehtiyot qismlar', 'запчасти', 'zapchast', 'запчасть', 'zapchasti'],
  ['yer', 'земля', 'участок', 'uchastka', 'jer'],
  ["do'kon", 'магазин', 'dukon', 'dúkan'],
];

/**
 * Упрощённое отсечение окончаний для запросов (без словарей и морфологического анализатора).
 * Слова в индексе не меняются: поиск идёт по префиксу (to_tsquery «слово:*»), поэтому «mashina» в объявлении
 * уже находится по «mashin». Отсечение нужно, когда окончание есть в ЗАПРОСЕ: «машины», «телефонлар»,
 * «kvartirani», «siyirlar». Окончания записаны после транслитерации в латиницу (см. searchKey); набор
 * выбирается по алфавиту исходного слова: кириллица → русские падежные, латиница → узбекские/каракалпакские.
 * Срезается одно самое длинное окончание; остаток — не короче 4 букв (3 — для узбекского мн. числа),
 * а более короткая основа принимается, только если это слово из словаря синонимов («uylar» → «uy»).
 */
const SUFFIXES_TURKIC = [
  'larining', 'lerining', 'larimiz', 'larini', 'lerini', 'lardan', 'lerden', 'larda', 'lerde', 'larga', 'lerge',
  'lari', 'leri', 'ning', 'niki', 'dagi', 'degi', 'lar', 'ler', 'dan', 'den', 'tan', 'ten', 'ga', 'ge', 'ka', 'ke', 'qa', 'da', 'de', 'ta', 'te', 'ni', 'si',
].sort((a, b) => b.length - a.length);
const SUFFIXES_RU = [
  'yami', 'ami', 'ogo', 'ego', 'omu', 'emu', 'ymi', 'imi', 'aya', 'yaya', 'uyu', 'yuyu', 'yah', 'yam',
  'ov', 'ev', 'ey', 'oy', 'iy', 'yy', 'oe', 'ee', 'ye', 'ie', 'ah', 'am', 'om', 'em', 'i', 'a', 'y', 'u', 'e', 'o',
].sort((a, b) => b.length - a.length);

export type TokenScript = 'cyrillic' | 'latin';

export function stemToken(token: string, script: TokenScript = 'latin', isKnownWord: (w: string) => boolean = () => false): string {
  if (token.length < 4 || /^\d+$/.test(token)) return token;
  for (const suf of script === 'cyrillic' ? SUFFIXES_RU : SUFFIXES_TURKIC) {
    if (!token.endsWith(suf)) continue;
    const stem = token.slice(0, -suf.length);
    const min = script === 'latin' && (suf === 'lar' || suf === 'ler') ? 3 : 4;
    if (stem.length >= min || (stem.length >= 2 && isKnownWord(stem))) return stem;
  }
  return token;
}

let SYNONYM_INDEX = new Map<string, string[]>();

function buildSynonymIndex(groups: string[][]): Map<string, string[]> {
  const index = new Map<string, string[]>();
  for (const group of groups) {
    // Синонимы — только однословные ключи; многословные участвуют как фразы через FTS AND.
    const keys = Array.from(new Set(group.map(searchKey).filter((k) => k && !k.includes(' '))));
    if (keys.length < 2) continue;
    for (const k of keys) {
      const prev = index.get(k) ?? [];
      index.set(k, Array.from(new Set([...prev, ...keys])));
    }
  }
  return index;
}

const MAX_QUERY_TOKENS = 8;
const MIN_TOKEN_LENGTH = 2;

/** Токены запроса после нормализации (дедуплицированные, не длиннее 8 штук). */
export function queryTokens(query: string): string[] {
  return queryTokensWithScript(query).map((t) => t.token);
}

/** То же, с алфавитом исходного слова (для выбора набора окончаний). */
export function queryTokensWithScript(query: string): { token: string; script: TokenScript }[] {
  const seen = new Set<string>();
  const out: { token: string; script: TokenScript }[] = [];
  for (const word of query.split(/\s+/)) {
    const script: TokenScript = /[\u0400-\u04ff]/.test(word) ? 'cyrillic' : 'latin';
    for (const token of searchKey(word).split(' ')) {
      if (!token || seen.has(token) || (token.length < MIN_TOKEN_LENGTH && !/^\d+$/.test(token))) continue;
      seen.add(token);
      out.push({ token, script });
    }
  }
  return out.slice(0, MAX_QUERY_TOKENS);
}

/** Синонимы по основе слова: «mashinoy» → основа «mashin» → группа «mashina/avtomobil/…». */
let SYNONYM_STEM_INDEX = new Map<string, string[]>();

function buildStemIndex(index: Map<string, string[]>): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const [k, group] of index) {
    for (const st of [stemToken(k, 'latin'), stemToken(k, 'cyrillic')]) {
      out.set(st, Array.from(new Set([...(out.get(st) ?? []), ...group])));
    }
  }
  return out;
}

/** Заменить словарь синонимов (вызывает API после чтения таблицы search_synonym_groups). Пустой → по умолчанию. */
export function setSynonymGroups(groups: string[][]): void {
  SYNONYM_INDEX = buildSynonymIndex(groups.length ? groups : DEFAULT_SYNONYM_GROUPS);
  SYNONYM_STEM_INDEX = buildStemIndex(SYNONYM_INDEX);
}

const isSynonymWord = (w: string) => SYNONYM_INDEX.has(w);

/** Для каждого токена — список альтернатив: сам токен, его основа (если отсечено окончание) и синонимы. */
export function expandSynonyms(tokens: (string | { token: string; script: TokenScript })[]): string[][] {
  return tokens.map((item) => {
    const { token: t, script } = typeof item === 'string' ? { token: item, script: 'latin' as TokenScript } : item;
    const stem = stemToken(t, script, isSynonymWord);
    const group = SYNONYM_INDEX.get(t) ?? SYNONYM_INDEX.get(stem) ?? SYNONYM_STEM_INDEX.get(stem) ?? [];
    return Array.from(new Set([t, ...(stem !== t ? [stem] : []), ...group]));
  });
}

/**
 * Строит выражение для to_tsquery('simple', ...): (a:* | b:*) & (c:*).
 * Токены содержат только [a-z0-9], поэтому экранирование не требуется,
 * но всё равно фильтруем на всякий случай.
 */
export function buildTsQuery(query: string): string | null {
  const groups = expandSynonyms(queryTokensWithScript(query));
  const parts = groups
    .map((alts) => alts.filter((a) => /^[a-z0-9]+$/.test(a)).map((a) => `${a}:*`))
    .filter((alts) => alts.length > 0)
    .map((alts) => (alts.length === 1 ? alts[0]! : `(${alts.join(' | ')})`));
  return parts.length ? parts.join(' & ') : null;
}

// Начальный словарь (до загрузки из БД).
setSynonymGroups(DEFAULT_SYNONYM_GROUPS);
